import { Avatar } from '@mui/material';
import { bool, oneOf, shape, string } from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import companies from 'store/app/entities/companies/action';
import pictureModal from 'store/app/ui/modals/picture/action';
import isSuperAdminSelector from 'store/selectors/isSuperAdmin';
import initialsFromUsername from 'utils/initialsFromUsername';
import AvatarBadge from './AvatarBadge';

const avatarSx = (theme, { large, xl, hasAvatar, isPending }) => ({
  color: theme.palette.text.inverted,
  backgroundColor: isPending ? theme.palette.secondary.extraLight : theme.palette.primary.main,
  boxShadow: theme.shadows[1],
  ...(large && { width: 44, height: 44 }),
  ...(xl && { width: 80, height: 80 }),
  ...(hasAvatar && { boxShadow: theme.shadows[1] }),
});

const EmployeeAvatar = ({ xl = false, large = false, status = 'active', employee, className }) => {
  const dispatch = useDispatch();
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const openModal = useCallback(() => {
    if (large && employee.avatar) {
      dispatch(pictureModal.open(employee.avatar));
    }
    if (xl && employee.avatar) {
      dispatch(pictureModal.open(employee.avatar));
    }
  }, [dispatch, employee.avatar, large, xl]);
  const handleClick = useCallback(
    e => {
      if (isSuperAdmin) {
        e.stopPropagation();
        dispatch(companies.toggleAcceptTerms(status === 'pending', employee._id));
      }
    },
    [dispatch, employee._id, isSuperAdmin, status],
  );

  const initials = useMemo(() => initialsFromUsername(employee.name), [employee.name]);

  return (
    <AvatarBadge
      onClick={handleClick}
      enable={isSuperAdmin || ['pending', 'admin'].includes(status)}
      className={className}
      status={status}
    >
      <Avatar
        onClick={openModal}
        sx={theme =>
          avatarSx(theme, {
            large,
            xl,
            hasAvatar: !!employee.avatar,
            isPending: status === 'pending',
          })
        }
        src={employee.avatar}
      >
        {employee.avatar ? null : initials}
      </Avatar>
    </AvatarBadge>
  );
};

EmployeeAvatar.propTypes = {
  className: string,
  status: oneOf(['pending', 'active', 'admin']),
  large: bool,
  xl: bool,
  employee: shape({ name: string, avatar: string }).isRequired,
};

const isEqual = (prev, next) => {
  if (prev.xl !== next.xl) return false;
  if (prev.large !== next.large) return false;
  if (prev.status !== next.status) return false;
  if (prev.employee.name !== next.employee.name) return false;
  return prev.employee.avatar === next.employee.avatar;
};
export default memo(EmployeeAvatar, isEqual);
